import { type PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { Modal, ModalBody, ModalContent, ModalHeader, Select, SelectItem } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { buildPdfPreviewEmbedUrl, isPdfFile, pdfFileKey } from './claimIntakeDocumentView';

const PANEL_MIN_W = 280;
const PANEL_MAX_RATIO = 0.75;
const PDF_PREVIEW_WIDE_MIN_PX = 900;

export type ClaimIntakePdfPreviewPanelProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  pdfFiles: readonly File[];
  selectedFile: File | null;
  onSelectFile: (file: File) => void;
  width: number;
  onWidthChange: (width: number) => void;
  layoutRef: React.RefObject<HTMLDivElement | null>;
}>;

function useIsWidePdfPreview(): boolean {
  const [matches, setMatches] = useState(() =>
    typeof globalThis.window === 'object'
      ? globalThis.window.matchMedia(`(min-width: ${PDF_PREVIEW_WIDE_MIN_PX}px)`).matches
      : true
  );

  useEffect(() => {
    if (typeof globalThis.window !== 'object') return;
    const mq = globalThis.window.matchMedia(`(min-width: ${PDF_PREVIEW_WIDE_MIN_PX}px)`);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return matches;
}

function clampPanelWidth(px: number, containerWidth: number): number {
  const maxW = Math.round(containerWidth * PANEL_MAX_RATIO);
  return Math.round(Math.max(PANEL_MIN_W, Math.min(maxW, px)));
}

function resolveActiveFile(pdfFiles: readonly File[], selectedFile: File | null): File {
  if (selectedFile && isPdfFile(selectedFile)) return selectedFile;
  return pdfFiles[0];
}

type PdfPreviewToolbarProps = Readonly<{
  pdfFiles: readonly File[];
  activeFile: File;
  activeKey: string;
  onSelectFile: (file: File) => void;
  onClose: () => void;
}>;

function PdfPreviewToolbar({ pdfFiles, activeFile, activeKey, onSelectFile, onClose }: PdfPreviewToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-200 shrink-0 bg-[#F9FAFB] min-h-[40px]">
      <Icon icon="mdi:file-pdf-box" className="text-red-500 shrink-0" width={16} />
      {pdfFiles.length > 1 ? (
        <Select
          size="sm"
          aria-label={t('Select PDF')}
          selectedKeys={new Set([activeKey])}
          onSelectionChange={(keys) => {
            const key = String(Array.from(keys)[0] ?? '');
            const idx = pdfFiles.findIndex((file, index) => pdfFileKey(file, index) === key);
            if (idx >= 0) onSelectFile(pdfFiles[idx]);
          }}
          classNames={{
            base: 'flex-1 min-w-0',
            trigger: 'min-h-8 h-8 bg-white shadow-none border border-gray-200',
            value: 'text-xs font-medium',
          }}
          popoverProps={{ classNames: { content: 'max-w-[min(100vw-2rem,24rem)]' } }}
        >
          {pdfFiles.map((file, index) => (
            <SelectItem key={pdfFileKey(file, index)} textValue={file.name}>
              <span className="truncate block max-w-full">{file.name}</span>
            </SelectItem>
          ))}
        </Select>
      ) : (
        <span className="text-xs font-medium text-gray-900 truncate flex-1 min-w-0" title={activeFile.name}>
          {activeFile.name}
        </span>
      )}
      <button
        type="button"
        className="shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        onClick={onClose}
        aria-label={t('Close PDF preview')}
      >
        <Icon icon="mdi:close" width={18} />
      </button>
    </div>
  );
}

type PdfPreviewFrameProps = Readonly<{
  iframeSrc: string | null;
  title: string;
  emptyLabel: string;
}>;

function PdfPreviewFrame({ iframeSrc, title, emptyLabel }: PdfPreviewFrameProps) {
  return (
    <div className="flex-1 min-h-0 bg-gray-100">
      {iframeSrc ? (
        <iframe src={iframeSrc} title={title} className="w-full h-full border-none" />
      ) : (
        <div className="h-full flex items-center justify-center text-sm text-gray-400 px-3 text-center">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

export function ClaimIntakePdfPreviewPanel({
  isOpen,
  onClose,
  pdfFiles,
  selectedFile,
  onSelectFile,
  width,
  onWidthChange,
  layoutRef,
}: ClaimIntakePdfPreviewPanelProps) {
  const { t } = useTranslation();
  const isWideViewport = useIsWidePdfPreview();
  const dragRef = useRef<null | { startX: number; startW: number }>(null);

  const activeFile = resolveActiveFile(pdfFiles, selectedFile);
  const activeFileIndex = pdfFiles.findIndex(
    (f) =>
      f.name === activeFile.name &&
      f.lastModified === activeFile.lastModified &&
      f.size === activeFile.size
  );
  const activeKey = pdfFileKey(activeFile, activeFileIndex >= 0 ? activeFileIndex : 0);

  const previewUrl = useMemo(() => {
    if (!activeFile) return null;
    return URL.createObjectURL(activeFile);
  }, [activeFile]);

  const iframeSrc = previewUrl ? buildPdfPreviewEmbedUrl(previewUrl) : null;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onResizePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onResizePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const containerWidth = layoutRef.current?.getBoundingClientRect().width ?? globalThis.window.innerWidth;
    onWidthChange(clampPanelWidth(d.startW + (e.clientX - d.startX), containerWidth));
  };

  const onResizePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
  };

  if (!isOpen || pdfFiles.length === 0) return null;

  const toolbar = (
    <PdfPreviewToolbar
      pdfFiles={pdfFiles}
      activeFile={activeFile}
      activeKey={activeKey}
      onSelectFile={onSelectFile}
      onClose={onClose}
    />
  );

  const frame = (
    <PdfPreviewFrame
      iframeSrc={iframeSrc}
      title={activeFile.name}
      emptyLabel={t('Select a PDF to preview')}
    />
  );

  if (!isWideViewport) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        placement="center"
        scrollBehavior="inside"
        hideCloseButton
        classNames={{
          wrapper: 'p-0 w-screen h-screen max-w-none max-h-none min-w-full min-h-full',
          base: 'w-full h-full max-w-none max-h-none rounded-none m-0 flex flex-col',
          body: 'p-0 flex-1 min-h-0 overflow-hidden',
          header: 'p-0 shrink-0',
          backdrop: 'bg-black/50',
        }}
      >
        <ModalContent className="h-[100dvh] max-h-[100dvh] flex flex-col">
          <ModalHeader className="p-0">{toolbar}</ModalHeader>
          <ModalBody>{frame}</ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <div
      className="relative shrink-0 h-full min-h-0 flex flex-col bg-white border-r border-gray-200 overflow-hidden"
      style={{ width, minWidth: PANEL_MIN_W }}
    >
      {toolbar}
      {frame}
      <hr
        aria-orientation="vertical"
        aria-label={t('Resize PDF panel')}
        className="absolute right-0 top-0 bottom-0 m-0 p-0 h-full border-y-0 border-x border-transparent w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-primary/25 transition-colors"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
      />
    </div>
  );
}
